import firstYardNavRaw from "../../../production/contracts/i0/first-yard-nav-v1.json?raw";
import {
  isSegmentWalkable,
  validateNavLayout,
  type NavSnapshot,
  type ReadonlyPointXZ,
} from "../../navigation/base/index.ts";

const MICROWU_PER_MILLIWU = 1_000;

function scalePoint(point: ReadonlyPointXZ): ReadonlyPointXZ {
  return Object.freeze([
    point[0] * MICROWU_PER_MILLIWU,
    point[1] * MICROWU_PER_MILLIWU,
  ] as const);
}

function createFirstYardMicroNavSnapshot(): Readonly<NavSnapshot> {
  const source = validateNavLayout(JSON.parse(firstYardNavRaw) as unknown);
  return Object.freeze({
    ...source,
    regions: Object.freeze(source.regions.map((region) => Object.freeze({
      ...region,
      outerPolygonCCW: Object.freeze(region.outerPolygonCCW.map(scalePoint)),
    }))),
    obstacles: Object.freeze(source.obstacles.map((obstacle) => Object.freeze({
      ...obstacle,
      rawObstacleAabb: Object.freeze({
        xMin: obstacle.rawObstacleAabb.xMin * MICROWU_PER_MILLIWU,
        xMax: obstacle.rawObstacleAabb.xMax * MICROWU_PER_MILLIWU,
        zMin: obstacle.rawObstacleAabb.zMin * MICROWU_PER_MILLIWU,
        zMax: obstacle.rawObstacleAabb.zMax * MICROWU_PER_MILLIWU,
      }),
      paddedObstacleFootprint: Object.freeze({
        xMin: obstacle.paddedObstacleFootprint.xMin * MICROWU_PER_MILLIWU,
        xMax: obstacle.paddedObstacleFootprint.xMax * MICROWU_PER_MILLIWU,
        zMin: obstacle.paddedObstacleFootprint.zMin * MICROWU_PER_MILLIWU,
        zMax: obstacle.paddedObstacleFootprint.zMax * MICROWU_PER_MILLIWU,
      }),
    }))),
  }) as unknown as Readonly<NavSnapshot>;
}

const FIRST_YARD_MICRO_NAV = createFirstYardMicroNavSnapshot();

function validSegment(from: ReadonlyPointXZ, to: ReadonlyPointXZ): boolean {
  return isSegmentWalkable(FIRST_YARD_MICRO_NAV, from, to);
}

export interface ManualChainMovementPoint {
  readonly xMicroWU: number;
  readonly zMicroWU: number;
}

export function resolveFirstYardMovement(
  from: Readonly<ManualChainMovementPoint>,
  deltaXMicroWU: number,
  deltaZMicroWU: number,
): Readonly<ManualChainMovementPoint> {
  const fromPoint = Object.freeze([from.xMicroWU, from.zMicroWU] as const);
  const desired = Object.freeze([
    from.xMicroWU + deltaXMicroWU,
    from.zMicroWU + deltaZMicroWU,
  ] as const);
  if (validSegment(fromPoint, desired)) {
    return Object.freeze({ xMicroWU: desired[0], zMicroWU: desired[1] });
  }

  let resolved = fromPoint;
  if (deltaXMicroWU !== 0) {
    const xCandidate = Object.freeze([from.xMicroWU + deltaXMicroWU, from.zMicroWU] as const);
    if (validSegment(resolved, xCandidate)) resolved = xCandidate;
  }
  if (deltaZMicroWU !== 0) {
    const zCandidate = Object.freeze([resolved[0], from.zMicroWU + deltaZMicroWU] as const);
    if (validSegment(resolved, zCandidate)) resolved = zCandidate;
  }
  return Object.freeze({ xMicroWU: resolved[0], zMicroWU: resolved[1] });
}
